// Globals
import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Input, Skeleton } from '@/ui';
import { FormItem, IFormInstance } from '@/components';
import { CheckmarkIcon } from '@/icons';
import { StyledForm, StyledTitle, StyledWrapper, StyledButton, FormColumn } from './CreateFilterValueFormStyles';

// Modules
import { LOCALES_FIELD_NAME, LocalesFormFields } from '@/modules/Locales';

// Models
import { IFilterValue } from '@/models/filters.model';
import { CategoryLocaleCodes } from '@/models/categories.model';
import { DEFAULT_LOCALE } from '@/models/localization.model';

// Helpers
import { FiltersValuesFieldNames, TCreateFilterValue } from '../../helpers/types';
import { useGetSlugRule } from '@/hooks';

interface ICreateFilterValueForm {
  handleSubmit: (values: TCreateFilterValue) => void;
  isLoading: boolean;
  form: IFormInstance;
  initialValues?: IFilterValue | null;
}

const initialLocale = {
  [CategoryLocaleCodes.UK]: { title: '' },
  [CategoryLocaleCodes.RU]: { title: '' },
};

const CreateFilterValueForm = ({ handleSubmit, isLoading, initialValues, form }: ICreateFilterValueForm) => {
  const isEdit = !!initialValues?.id;
  const { t } = useTranslation(['categories', 'common']);
  const slugRule = useGetSlugRule({ t });

  const placeholder = t('common_placeholder_input', { ns: 'common' });

  if (isLoading) return <Skeleton />;

  return (
    <StyledWrapper>
      <StyledTitle>
        {t(isEdit ? 'filter_values_edit_form_label' : 'filter_values_add_form_label', {
          title: initialValues?.locale?.[DEFAULT_LOCALE]?.title,
        })}
      </StyledTitle>
      <StyledForm onFinish={handleSubmit} initialValues={initialValues ?? { locale: initialLocale }} form={form}>
        <FormColumn>
          <FormItem name={LOCALES_FIELD_NAME}>
            <LocalesFormFields labelPrefix={'filter_values_create_'} />
          </FormItem>
          <FormItem rules={[slugRule]} name={FiltersValuesFieldNames.SLUG} label={t('filter_values_create_alias')}>
            <Input placeholder={placeholder} />
          </FormItem>
        </FormColumn>

        <StyledButton htmlType="submit" loading={isLoading}>
          <CheckmarkIcon /> {t('filter_values_save')}
        </StyledButton>
      </StyledForm>
    </StyledWrapper>
  );
};

export default CreateFilterValueForm;
